import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Activity, ChevronRight, X, Syringe, Scale, Utensils, MessageSquare, UserPlus, UserCog, Clock, ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { createPortal } from "react-dom";

const TEAL = "#0D9488";
const ROLE_COLORS = { admin: "bg-amber-100 text-amber-700", moderator: "bg-teal-50 text-teal-700", user: "bg-slate-100 text-slate-600" };
const EVENT_META = {
  shot:      { icon: Syringe,       color: "text-teal-700" },
  weight:    { icon: Scale,         color: "text-blue-700" },
  nutrition: { icon: Utensils,      color: "text-orange-700" },
  community: { icon: MessageSquare, color: "text-purple-700" },
  coach:     { icon: UserPlus,      color: "text-emerald-700" },
  profile:   { icon: UserCog,       color: "text-slate-600" },
};

function relative(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toISOString().slice(0, 10);
}

export default function AdminUserActivityCard() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // row id
  const [drawerUser, setDrawerUser] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/user-activity", { params: { q: debouncedQ, page, page_size: 20 } })
      .then(({ data }) => {
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setHasMore(!!data.has_more);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [debouncedQ, page]);
  useEffect(() => { load(); }, [load]);

  const pageMax = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="admin-user-activity-card">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>User activity</div>
        <span className="text-[10px] text-slate-400 tabular-nums ml-1">{total.toLocaleString()}</span>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Support triage</h3>
      <p className="text-sm text-slate-500 mb-4">
        Every user with their last login and a rolling summary of the actions they've taken. Search by name, email or handle, then tap any row for a full timeline.
      </p>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search users…"
          className="pl-9 bg-slate-50 border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
          data-testid="admin-user-activity-search"
        />
        {q && (
          <button type="button" onClick={() => setQ("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Clear" data-testid="admin-user-activity-search-clear">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-1.5">
          {[0,1,2,3,4].map((i) => <div key={i} className="h-16 bg-slate-50 border border-slate-100 rounded animate-pulse" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-6 text-center"
             data-testid="admin-user-activity-empty">
          {debouncedQ ? "No users match this search." : "No users yet."}
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="admin-user-activity-list">
          {rows.map((u) => {
            const isOpen = expanded === u.id;
            return (
              <li key={u.id} className="border border-slate-100 rounded-lg overflow-hidden"
                  data-testid={`admin-user-activity-row-${u.id}`}>
                <div className="p-3 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  <button type="button" onClick={() => setExpanded(isOpen ? null : u.id)}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                          data-testid={`admin-user-activity-toggle-${u.id}`}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-[10px] text-white shrink-0"
                         style={{ background: "linear-gradient(135deg,#94A3B8 0%,#475569 100%)" }}>
                      {(u.handle || u.name || u.email || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {u.name || `@${u.handle}`}
                        </div>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold ${ROLE_COLORS[u.role] || ROLE_COLORS.user}`}>
                          {u.role}
                        </span>
                        {u.is_coach && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-teal-50 text-teal-700">Coach</span>
                        )}
                        {u.banned && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-rose-100 text-rose-700">Banned</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono-num truncate">{u.email}</div>
                    </div>
                    <div className="text-right shrink-0 hidden sm:block">
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center justify-end gap-1">
                        <Clock className="w-2.5 h-2.5" /> Last login
                      </div>
                      <div className="text-[12px] font-semibold text-slate-800 font-mono-num" data-testid={`admin-user-activity-last-login-${u.id}`}>
                        {u.last_login_at ? relative(u.last_login_at) : "Never"}
                      </div>
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                  </button>
                </div>
                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/40 p-3" data-testid={`admin-user-activity-actions-${u.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-1">
                        <Activity className="w-3 h-3" /> Recent actions
                      </div>
                      <Button type="button" size="sm" variant="ghost"
                              onClick={() => setDrawerUser(u)}
                              className="h-6 text-teal-700 hover:bg-teal-50 text-[11px]"
                              data-testid={`admin-user-activity-full-timeline-${u.id}`}>
                        Full timeline <ChevronRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    </div>
                    {u.recent_actions.length === 0 ? (
                      <div className="text-[12px] text-slate-500 italic">No tracked actions yet.</div>
                    ) : (
                      <ul className="space-y-1">
                        {u.recent_actions.map((a, i) => <li key={i}><ActivityRow action={a} /></li>)}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100" data-testid="admin-user-activity-pagination">
          <div className="text-[11px] text-slate-500 font-mono-num">
            Page {page} of {pageMax} · {total.toLocaleString()} users
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="admin-user-activity-prev">
              Prev
            </Button>
            <Button type="button" size="sm" variant="outline"
                    disabled={!hasMore || loading}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="admin-user-activity-next">
              Next
            </Button>
          </div>
        </div>
      )}

      {drawerUser && (
        <UserTimelineDrawer user={drawerUser} onClose={() => setDrawerUser(null)} />
      )}
    </div>
  );
}

function ActivityRow({ action }) {
  const meta = EVENT_META[action.type] || EVENT_META.profile;
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-2 text-[13px] py-1">
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.color}`} />
      <div className="flex-1 min-w-0">
        <div className="text-slate-800 truncate">{action.label}</div>
      </div>
      <div className="text-[10px] text-slate-400 font-mono-num shrink-0 pt-0.5">{relative(action.at)}</div>
    </div>
  );
}

function UserTimelineDrawer({ user, onClose }) {
  const [actions, setActions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/users/${user.id}/activity`, { params: { limit: 50 } })
      .then(({ data }) => setActions(data.actions || []))
      .catch((err) => { toast.error(formatApiError(err)); setActions([]); })
      .finally(() => setLoading(false));
  }, [user.id]);

  return createPortal(
    <div className="fixed inset-0 z-[9985] bg-slate-900/50 backdrop-blur-sm flex items-stretch justify-end"
         onClick={onClose} data-testid="admin-user-timeline-overlay">
      <div className="bg-slate-50 w-full max-w-lg h-full overflow-y-auto shadow-2xl border-l border-slate-200"
           onClick={(e) => e.stopPropagation()} data-testid="admin-user-timeline-drawer">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center gap-3">
          <button type="button" onClick={onClose}
                  className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm"
                  data-testid="admin-user-timeline-close">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="ml-2 flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>User timeline</div>
            <div className="font-display font-bold text-lg text-slate-900 truncate">
              {user.name || `@${user.handle}`} <span className="text-slate-400 font-normal text-sm">· {user.email}</span>
            </div>
            <div className="text-[11px] text-slate-500">
              Last login <b className="text-slate-800">{user.last_login_at ? relative(user.last_login_at) : "never"}</b> · Joined <b className="text-slate-800">{user.created_at ? user.created_at.slice(0, 10) : "—"}</b>
            </div>
          </div>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="space-y-1.5">
              {[0,1,2,3,4].map((i) => <div key={i} className="h-10 bg-white border border-slate-100 rounded animate-pulse" />)}
            </div>
          ) : actions.length === 0 ? (
            <div className="text-sm text-slate-500 italic border border-dashed border-slate-200 rounded-md p-6 text-center">
              No tracked activity for this user yet.
            </div>
          ) : (
            <ul className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              {actions.map((a, i) => (
                <li key={i} className="px-4 py-2.5">
                  <ActivityRow action={a} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
